import { Eye, Loader2, X } from 'lucide-react'
import { decodeUploadFileName } from '@numeo/types-core'
import { useState } from 'react'
import { ConfirmDocumentRemovalDialog } from './ConfirmDocumentRemovalDialog'
import { DocumentFileName } from './DocumentFileName'
import { VehicleDocumentRemovalAlert } from './VehicleDocumentRemovalAlert'

interface StoredProfileDocumentProps {
    fileName: string
    removalIncomplete: boolean
    canRemove: boolean
    busy: boolean
    removing: boolean
    onOpen: () => void
    onRemove: () => void
    removalDescription?: string
}

export const StoredProfileDocument = ({
    fileName,
    removalIncomplete,
    canRemove,
    busy,
    removing,
    onOpen,
    onRemove,
    removalDescription,
}: StoredProfileDocumentProps) => {
    const [confirmRemoval, setConfirmRemoval] = useState(false)
    // A name stored before uploads decoded it is garbled; the chip, the remove dialog and the retry alert all show it as uploaded.
    const readable = decodeUploadFileName(fileName)

    if (removalIncomplete) {
        return <VehicleDocumentRemovalAlert fileName={readable} busy={busy || !canRemove} onRetry={onRemove} />
    }

    return (
        <span className="flex min-w-0 items-center rounded-lg border border-border bg-surface">
            <button
                type="button"
                className="flex min-w-0 items-center gap-1 px-2 py-1 text-xs hover:text-accent"
                aria-label={`Preview ${readable}`}
                onClick={(event) => {
                    event.stopPropagation()
                    onOpen()
                }}
            >
                <Eye className="h-3 w-3 shrink-0" />
                <DocumentFileName fileName={fileName} />
            </button>
            {canRemove && (
                <button
                    type="button"
                    className="rounded-r-lg px-2 py-1 text-foreground-muted hover:bg-danger-soft hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    aria-label={`Remove ${readable}`}
                    disabled={busy}
                    onClick={(event) => {
                        event.stopPropagation()
                        setConfirmRemoval(true)
                    }}
                >
                    {removing ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                </button>
            )}
            <ConfirmDocumentRemovalDialog
                fileName={confirmRemoval ? readable : null}
                description={removalDescription}
                onClose={() => setConfirmRemoval(false)}
                onConfirm={() => {
                    setConfirmRemoval(false)
                    onRemove()
                }}
            />
        </span>
    )
}
